import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart, LabelList, Legend, Line,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis
} from 'recharts';
import { getBusinessAnalytics, getFinanceLedger } from '../api';
import CollapsibleFilters from './CollapsibleFilters';
import InfoTip from './InfoTip';
import OrderDetailsModal from './OrderDetailsModal';

// v3_fix_issues #7 & #8 - the website owner's view of PLATFORM performance, the mirror of the
// rental owner's own "Earnings" tab. Charts + KPIs (present vs. the previous period of equal
// length) + which vertical / customer / rental owner is doing best, plus a filterable
// booking-level revenue ledger. All data comes from the platform-wide /api/admin/analytics
// endpoints (not vertical-scoped), so this component is identical in every vertical's UI.
//
// Chart colours are the data-viz reference palette's categorical slots (blue / orange / aqua),
// used within their validated bounds - blue+orange is a passing adjacent pair, and the 3-vertical
// bar uses the first three slots (which pass all-pairs). Colour follows the ENTITY (a fixed
// vertical->hue map), never its rank.

const PALETTE = {
  light: {
    revenue: '#2a78d6', bookings: '#eb6834', confirmed: '#1baf7a',
    grid: '#e6e6e3', axis: '#52514e',
    vertical: { car_rental: '#2a78d6', hospitality: '#eb6834', jewelry: '#1baf7a' }
  },
  dark: {
    revenue: '#3987e5', bookings: '#d95926', confirmed: '#199e70',
    grid: '#3a3a38', axis: '#c3c2b7',
    vertical: { car_rental: '#3987e5', hospitality: '#d95926', jewelry: '#199e70' }
  }
};

function usePalette() {
  const [dark, setDark] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return dark ? PALETTE.dark : PALETTE.light;
}

const RANGE_OPTIONS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'last_90_days', label: 'Last 90 days' },
  { key: 'this_year', label: 'This financial year' },
  { key: 'last_year', label: 'Last financial year' },
  { key: 'last_12_months', label: 'Last 12 months' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom range' }
];

const VERTICAL_OPTIONS = [
  { key: '', label: 'All verticals' },
  { key: 'car_rental', label: 'Car rental' },
  { key: 'hospitality', label: 'Hospitality' },
  { key: 'jewelry', label: 'Jewellery' }
];
const VERTICAL_LABEL = { car_rental: 'Car rental', hospitality: 'Hospitality', jewelry: 'Jewellery' };

const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };

const KPIS = [
  { key: 'platformRevenue', label: 'Platform revenue', money: true, help: "The website's cut = booking commission + platform fee, over non-cancelled bookings." },
  { key: 'grossBookingValue', label: 'Gross booking value', money: true, help: 'What customers paid across all bookings (rental + fee + delivery), deposits excluded.' },
  { key: 'bookings', label: 'Bookings', help: 'Every booking created in the period, any status.' },
  { key: 'confirmed', label: 'Confirmed', help: 'Bookings a rental owner accepted.' },
  { key: 'cancelled', label: 'Cancellations', help: 'Bookings cancelled by anyone in the period.', invertDelta: true },
  { key: 'avgOrderValue', label: 'Avg. order value', money: true, help: 'Gross booking value divided by non-cancelled bookings.' },
  { key: 'uniqueCustomers', label: 'Customers', help: 'Distinct customers who booked in the period.' },
  { key: 'activeOwners', label: 'Active owners', help: 'Rental owners with at least one booking in the period.' }
];

const money = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const moneyShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(n / 1e3).toFixed(1)}k`;
  return `₹${Math.round(n || 0)}`;
};

function fmtBucket(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function Delta({ d, invert }) {
  if (!d || !d.absolute) return <span className="text-muted small">no change</span>;
  const up = d.absolute > 0;
  const good = invert ? !up : up;
  return (
    <span className={`small ${good ? 'text-success' : 'text-danger'}`}>
      {up ? '▲' : '▼'} {Math.abs(d.absolute).toLocaleString('en-IN')}
      {d.pct != null ? ` (${Math.abs(d.pct)}%)` : ''}
    </span>
  );
}

function ChartCard({ title, subtitle, children, height = 260 }) {
  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title as="h6" className="mb-0">{title}</Card.Title>
        {subtitle && <div className="text-muted small mb-2">{subtitle}</div>}
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
}

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v != null));

export default function BusinessAnalytics() {
  const pal = usePalette();

  // Shared filters (drive both the analytics and the ledger).
  const [range, setRange] = useState('this_month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [vertical, setVertical] = useState('');
  const [granularity, setGranularity] = useState('');

  // Ledger-only filters.
  const [ledgerFilters, setLedgerFilters] = useState({ owner: '', customer: '', status: '', paymentStatus: '', payoutStatus: '' });
  const [sort, setSort] = useState('date');
  const [page, setPage] = useState(1);
  const [ownerMetric, setOwnerMetric] = useState('confirmed');
  const [detailsBooking, setDetailsBooking] = useState(null);

  const customIncomplete = range === 'custom' && (!from || !to);

  const baseParams = useMemo(() => {
    const p = { range };
    if (range === 'custom') { p.from = from; p.to = to; }
    if (vertical) p.vertical = vertical;
    return p;
  }, [range, from, to, vertical]);

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAnalytics = useCallback(async () => {
    if (customIncomplete) return;
    setLoading(true);
    setError('');
    try {
      const params = { ...baseParams };
      if (granularity) params.granularity = granularity;
      setAnalytics(await getBusinessAnalytics(params));
    } catch {
      setError('Could not load business analytics.');
    } finally {
      setLoading(false);
    }
  }, [baseParams, granularity, customIncomplete]);

  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [ledgerError, setLedgerError] = useState('');

  const loadLedger = useCallback(async () => {
    if (customIncomplete) return;
    setLedgerLoading(true);
    setLedgerError('');
    try {
      const params = { ...baseParams, ...clean(ledgerFilters), sort, page, limit: 20 };
      setLedger(await getFinanceLedger(params));
    } catch {
      setLedgerError('Could not load the finance ledger.');
    } finally {
      setLedgerLoading(false);
    }
  }, [baseParams, ledgerFilters, sort, page, customIncomplete]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => { loadLedger(); }, [loadLedger]);
  // Any change to the shared filters, ledger filters, or sort resets pagination.
  useEffect(() => { setPage(1); }, [baseParams, ledgerFilters, sort]);

  const byVerticalData = useMemo(
    () => (analytics?.byVertical || []).map((r) => ({ ...r, label: VERTICAL_LABEL[r.vertical] || r.vertical })),
    [analytics]
  );
  const topCustomerData = useMemo(
    () => (analytics?.topCustomers || []).slice(0, 8).map((r) => ({ ...r, label: r.name || r.email })),
    [analytics]
  );
  const topOwnerData = useMemo(
    () => (analytics?.topOwners || []).slice(0, 8).map((r) => ({ ...r, label: r.name || r.email || r.ownerId })),
    [analytics]
  );

  const s = analytics?.summary;

  return (
    <div className="mb-4">
      {/* ---- Filters ---- */}
      <CollapsibleFilters label="Filters" className="mb-3">
        <Row className="g-2 align-items-end">
          <Col xs={6} md={3}>
            <Form.Label className="small mb-0">Period</Form.Label>
            <Form.Select size="sm" aria-label="Period" value={range} onChange={(e) => setRange(e.target.value)}>
              {RANGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </Form.Select>
          </Col>
          {range === 'custom' && (
            <>
              <Col xs={6} md={2}>
                <Form.Label className="small mb-0">From</Form.Label>
                <Form.Control size="sm" type="date" aria-label="From date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small mb-0">To</Form.Label>
                <Form.Control size="sm" type="date" aria-label="To date" value={to} onChange={(e) => setTo(e.target.value)} />
              </Col>
            </>
          )}
          <Col xs={6} md={3}>
            <Form.Label className="small mb-0">Vertical</Form.Label>
            <Form.Select size="sm" aria-label="Vertical" value={vertical} onChange={(e) => setVertical(e.target.value)}>
              {VERTICAL_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label className="small mb-0">Granularity</Form.Label>
            <Form.Select size="sm" aria-label="Granularity" value={granularity} onChange={(e) => setGranularity(e.target.value)}>
              <option value="">Auto</option>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </Form.Select>
          </Col>
        </Row>
      </CollapsibleFilters>

      {customIncomplete && <Alert variant="light" className="py-2 small">Pick a start and end date for the custom range.</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* ---- KPI cards ---- */}
      {loading && !analytics ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : s ? (
        <>
          <div className="text-muted small mb-2">
            {analytics.range?.label || 'Selected period'}
            {analytics.previous ? ' — compared with the preceding period of equal length' : ''}
          </div>
          <Row xs={2} sm={3} lg={4} className="g-2 mb-3">
            {KPIS.map((k) => (
              <Col key={k.key}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="text-muted small d-flex align-items-center">
                      {k.label}<InfoTip text={k.help} />
                    </div>
                    <div className="fs-5 fw-semibold">
                      {k.money ? money(s[k.key]) : (s[k.key] ?? 0).toLocaleString('en-IN')}
                    </div>
                    <Delta d={analytics.delta?.[k.key]} invert={k.invertDelta} />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ---- Charts ---- */}
          <Row>
            <Col lg={6}>
              <ChartCard title="Platform revenue over time" subtitle="Commission + platform fee, non-cancelled bookings">
                <AreaChart data={analytics.timeseries} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={pal.grid} vertical={false} />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis tickFormatter={moneyShort} tick={{ fill: pal.axis, fontSize: 11 }} width={56} />
                  <RTooltip formatter={(v) => money(v)} labelFormatter={fmtBucket} />
                  <Area
                    type="monotone" dataKey="platformRevenue" name="Platform revenue"
                    stroke={pal.revenue} strokeWidth={2} fill={pal.revenue} fillOpacity={0.15}
                  />
                </AreaChart>
              </ChartCard>
            </Col>
            <Col lg={6}>
              <ChartCard title="Bookings over time" subtitle="All bookings vs. those confirmed by owners">
                <ComposedChart data={analytics.timeseries} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={pal.grid} vertical={false} />
                  <XAxis dataKey="bucket" tickFormatter={fmtBucket} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: pal.axis, fontSize: 11 }} width={32} />
                  <RTooltip labelFormatter={fmtBucket} />
                  <Legend />
                  <Bar dataKey="bookings" name="All bookings" fill={pal.bookings} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Line type="monotone" dataKey="confirmed" name="Confirmed" stroke={pal.confirmed} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ChartCard>
            </Col>
          </Row>

          <Row>
            <Col lg={4}>
              <ChartCard title="Revenue by vertical" height={220}>
                <BarChart data={byVerticalData} layout="vertical" margin={{ top: 4, right: 44, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={pal.grid} horizontal={false} />
                  <XAxis type="number" tickFormatter={moneyShort} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={84} tick={{ fill: pal.axis, fontSize: 12 }} />
                  <RTooltip formatter={(v) => money(v)} />
                  <Bar dataKey="platformRevenue" name="Platform revenue" radius={[0, 4, 4, 0]} maxBarSize={26}>
                    {byVerticalData.map((e) => (
                      <Cell key={e.vertical} fill={pal.vertical[e.vertical] || pal.revenue} />
                    ))}
                    <LabelList dataKey="platformRevenue" position="right" formatter={moneyShort} style={{ fill: pal.axis, fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Col>
            <Col lg={4}>
              <ChartCard title="Top customers" subtitle="By gross booking value" height={220}>
                <BarChart data={topCustomerData} layout="vertical" margin={{ top: 4, right: 44, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={pal.grid} horizontal={false} />
                  <XAxis type="number" tickFormatter={moneyShort} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={110} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <RTooltip formatter={(v) => money(v)} />
                  <Bar dataKey="grossBookingValue" name="Gross booking value" fill={pal.revenue} radius={[0, 4, 4, 0]} maxBarSize={20}>
                    <LabelList dataKey="grossBookingValue" position="right" formatter={moneyShort} style={{ fill: pal.axis, fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Col>
            <Col lg={4}>
              <ChartCard
                title="Top rental owners"
                subtitle={(
                  <span className="d-inline-flex align-items-center gap-2">
                    <span>Ranked by</span>
                    <Form.Select
                      size="sm" style={{ width: 'auto' }} aria-label="Owner ranking metric" value={ownerMetric}
                      onChange={(e) => setOwnerMetric(e.target.value)}
                    >
                      <option value="confirmed">Confirmed bookings</option>
                      <option value="platformRevenue">Platform revenue</option>
                    </Form.Select>
                  </span>
                )}
                height={220}
              >
                <BarChart data={topOwnerData} layout="vertical" margin={{ top: 4, right: 44, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={pal.grid} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tickFormatter={ownerMetric === 'platformRevenue' ? moneyShort : undefined} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={110} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <RTooltip formatter={(v) => (ownerMetric === 'platformRevenue' ? money(v) : v)} />
                  <Bar
                    dataKey={ownerMetric}
                    name={ownerMetric === 'platformRevenue' ? 'Platform revenue' : 'Confirmed bookings'}
                    fill={pal.confirmed} radius={[0, 4, 4, 0]} maxBarSize={20}
                  >
                    <LabelList
                      dataKey={ownerMetric} position="right"
                      formatter={ownerMetric === 'platformRevenue' ? moneyShort : undefined}
                      style={{ fill: pal.axis, fontSize: 11 }}
                    />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      ) : (
        !error && <Alert variant="light">No data for this period.</Alert>
      )}

      {/* ---- Finance ledger (the filterable list) ---- */}
      <h6 className="mt-3">Finance ledger</h6>
      <div className="text-muted small mb-2">Every booking's contribution to platform revenue. Click a booking id for full order details.</div>

      <Row className="g-2 align-items-end mb-2">
        <Col xs={6} md={3}>
          <Form.Label className="small mb-0">Rental owner</Form.Label>
          <Form.Control
            size="sm" placeholder="name or id" aria-label="Rental owner"
            value={ledgerFilters.owner}
            onChange={(e) => setLedgerFilters((f) => ({ ...f, owner: e.target.value }))}
          />
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="small mb-0">Customer</Form.Label>
          <Form.Control
            size="sm" placeholder="name or email" aria-label="Customer"
            value={ledgerFilters.customer}
            onChange={(e) => setLedgerFilters((f) => ({ ...f, customer: e.target.value }))}
          />
        </Col>
        <Col xs={4} md={2}>
          <Form.Label className="small mb-0">Status</Form.Label>
          <Form.Select size="sm" aria-label="Status" value={ledgerFilters.status} onChange={(e) => setLedgerFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Any</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </Form.Select>
        </Col>
        <Col xs={4} md={2}>
          <Form.Label className="small mb-0">Payment</Form.Label>
          <Form.Select size="sm" aria-label="Payment status" value={ledgerFilters.paymentStatus} onChange={(e) => setLedgerFilters((f) => ({ ...f, paymentStatus: e.target.value }))}>
            <option value="">Any</option>
            <option value="PENDING">Pending</option>
            <option value="PAID">Paid</option>
            <option value="PARTIALLY_PAID">Partially paid</option>
            <option value="REFUNDED">Refunded</option>
            <option value="PARTIALLY_REFUNDED">Partially refunded</option>
            <option value="FAILED">Failed</option>
          </Form.Select>
        </Col>
        <Col xs={4} md={2}>
          <Form.Label className="small mb-0">Sort</Form.Label>
          <Form.Select size="sm" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="date">Newest</option>
            <option value="revenue">Revenue</option>
            <option value="gmv">Booking value</option>
          </Form.Select>
        </Col>
      </Row>

      {ledgerError && <Alert variant="danger" dismissible onClose={() => setLedgerError('')}>{ledgerError}</Alert>}

      {ledger?.totals && (
        <div className="small mb-2">
          <strong>Totals for this filter:</strong>{' '}
          Platform revenue {money(ledger.totals.platformRevenue)} · Gross booking value {money(ledger.totals.grossBookingValue)} · Commission {money(ledger.totals.platformCommission)} · Fees {money(ledger.totals.platformFee)}
          <span className="text-muted"> (excludes cancelled)</span>
        </div>
      )}

      {ledgerLoading && !ledger ? (
        <Spinner animation="border" size="sm" role="status" />
      ) : !ledger || ledger.data.length === 0 ? (
        <Alert variant="light">No bookings match this filter.</Alert>
      ) : (
        <>
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Booking</th>
                <th>Date</th>
                <th>Vertical</th>
                <th>Customer</th>
                <th>Rental owner</th>
                <th className="text-end">Booking value</th>
                <th className="text-end">Commission</th>
                <th className="text-end">Fee</th>
                <th className="text-end">Platform revenue</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Payout</th>
              </tr>
            </thead>
            <tbody>
              {ledger.data.map((b) => (
                <tr key={b._id}>
                  <td>
                    <Button variant="link" size="sm" className="p-0 font-monospace" onClick={() => setDetailsBooking(b)}>
                      {String(b._id).slice(-6).toUpperCase()}
                    </Button>
                  </td>
                  <td className="small">{b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="small">{VERTICAL_LABEL[b.vertical] || b.vertical}</td>
                  <td className="small">{b.customerName}</td>
                  <td className="small">{b.ownerName || '—'}</td>
                  <td className="text-end">{money(b.grossBookingValue)}</td>
                  <td className="text-end">{money(b.platformCommission)}</td>
                  <td className="text-end">{money(b.platformFee)}</td>
                  <td className="text-end fw-semibold">{b.status === 'cancelled' ? '—' : money(b.platformRevenue)}</td>
                  <td><Badge bg={STATUS_VARIANT[b.status] || 'secondary'}>{b.status}</Badge></td>
                  <td className="small">{(b.paymentStatus || '').replace(/_/g, ' ') || '—'}</td>
                  <td className="small">{(b.payoutStatus || '').replace(/_/g, ' ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <span className="small text-muted">
              Page {ledger.pagination.page} · {ledger.pagination.total} booking{ledger.pagination.total === 1 ? '' : 's'}
            </span>
            <Button size="sm" variant="outline-secondary" disabled={!ledger.pagination.hasMore} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </>
      )}

      <OrderDetailsModal
        booking={detailsBooking}
        itemName={detailsBooking?.itemName}
        show={!!detailsBooking}
        onHide={() => setDetailsBooking(null)}
      />
    </div>
  );
}
